import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '@fortawesome/free-regular-svg-icons';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import { getRecruitmentSources, getWorkflows } from '@/graphql/queries';
import { getSegments } from '@/graphql/small-queries';
import { upsertWorkflow } from '@/graphql/mutations';
import useBodyClick from '@/hooks/useBodyClick';
import useArrowKeys from '@/hooks/useArrowKeys';
import useLoadMore from '@/hooks/useLoadMore';
import useSubscription from '@/hooks/useSubscription';
import Header from '@/Header';
import { dash, downIcon, SpinnerLineDark, upIcon } from '@/utils';
import { getListSort, setListSort } from '@/utils/stickySettings';

const client = generateClient();

export default () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState({});
  const updateBusy = useCallback((id, value) => {
    setBusy(prevState => ({ ...prevState, [id]: value }));
  });

  const [search, setSearch] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const incSearchCount = useCallback(() => setSearchCount((p) => p + 1), []);
  const debounceSearch = useCallback(_.debounce((v) => { setDefaults(); setSearch(v) }, 500), []);

  const { field: defaultSortField, order: defaultSortOrder } = useMemo(() => getListSort({ key: window.location.pathname }) || { field: 'updated_at', order: 'desc' }, []);
  const [sortField, setSortField] = useState(defaultSortField && defaultSortOrder ? defaultSortField : undefined);
  const [sortOrder, setSortOrder] = useState(defaultSortField && defaultSortOrder ? { [defaultSortField]: defaultSortOrder } : {});

  const filters_ref = useRef();
  filters_ref.current = useMemo(() => {
    const filters = [];
    if ((search || '').trim().length > 0) {
      const s = search.trim();
      const sNum = Number(s);
      filters.push(
        ...(!isNaN(sNum) ? [{ id: { eq: sNum } }] : []),
        { name: { like: `%${s}%` } }
      );
    }
    const sorting = [
      { field: sortField, order: sortOrder[sortField] },
      // Always add id as a tie-breaker to avoid indeterminate order
      { field: 'id', order: 'asc' }
    ];
    return {
      filters,
      sorting
    };
  }, [search, sortField, sortOrder]);

  const { isLoadingMore, limit, loadMoreButton, offset, rows, setDefaults, setIsLoadingMore, setRows, setTotal } = useLoadMore({ position: 'absolute' });
  useSubscription({ filters_ref, limit_ref: limit, offset_ref: offset, rankings_key: 'workflows', setRows });

  // ------- State -------
  const [sidebarItem, _setSidebarItem] = useState();
  const setSidebarItem = useCallback(item => {
    _setSidebarItem(item);
 		const queryParams = new URLSearchParams(window.location.search);
    if (!item) {
      queryParams.delete('workflow_id');
    } else {
      queryParams.set('workflow_id', item.id);
    }
		navigate({ pathname: window.location.pathname, search: `?${queryParams.toString()}` }, { replace: true });
  }, []);

  // Holds maps for lookups
  const [recruitmentSources, setRecruitmentSources] = useState({});
  const [segments, setSegments] = useState({});

  const [loadCount, setLoadCount] = useState(0);
  const incLoadCount = () => setLoadCount(prevState => prevState + 1);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const workflow_id = parseInt(queryParams.get('workflow_id') || undefined, 10);
    if (workflow_id) {
      const item = rows.find(e => e.id === workflow_id);
      if (item) setSidebarItem(item);
    }
  }, [rows]);
  
  // ------- GraphQL Loader -------
  const load = useCallback(async () => {
    const { filters, sorting } = filters_ref.current;

    const res = await client.graphql({
      query: getWorkflows,
      variables: { filters, limit, offset, sorting }
    });

    const payload = res?.data?.getWorkflows;
    const data = payload?.workflows ?? [];
    // Collect all referenced ids within subject_filters_json
    const recruitment_id = [];
    const segment_id = [];
    for (let e of data) {
      let subject_filters_json;
      try { subject_filters_json = JSON.parse(e.subject_filters_json) } catch (_) {};
      if (subject_filters_json?.[0]?.subject_recruitment_sources?.recruitment_id) {
          recruitment_id.push(...(subject_filters_json[0].subject_recruitment_sources.recruitment_id.in || []));
      }
      if (subject_filters_json?.[0]?.segments?.id) {
          segment_id.push(...(subject_filters_json[0].segments.id.in || []));
      }
    }
    // Collect those which we do not have
    const _recruitment_id = _.uniq(_.difference(recruitment_id, Object.keys(recruitmentSources)));
    const _segment_id = _.uniq(_.difference(segment_id, Object.keys(segments)));
    if (_recruitment_id.length > 0) {
      const res = await client.graphql({
        query: getRecruitmentSources,
        variables: { id: _recruitment_id }
      });
      // Reduce to map
      const _res = res?.data?.getRecruitmentSources.recruitment_sources.reduce((prev, cur) => ({ ...prev, [cur.id]: cur }), {});
      setRecruitmentSources(prevState => ({ ...prevState, ..._res }));
    }
    if (_segment_id.length > 0) {
      const res = await client.graphql({
        query: getSegments,
        variables: { id: _segment_id }
      });
      // Reduce to map
      const _res = res?.data?.getSegments.segments.reduce((prev, cur) => ({ ...prev, [cur.id]: cur }), {});
      setSegments(prevState => ({ ...prevState, ..._res }));
    }
    setRows(prevState => (offset === 0 ? data : [...prevState, ...data]));
    setTotal(payload?.total_count ?? data.length);
    incLoadCount();
    setIsLoadingMore(false);
  }, [filters_ref.current, limit, offset]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  useBodyClick({ onClick: setSidebarItem });
  useArrowKeys({ rows, setSidebarRow: setSidebarItem, sidebarRow: sidebarItem });

  // ------- Actions -------
  const toggleActive = useCallback(async (id, currentActive) => {
    try {
      await client.graphql({
        query: upsertWorkflow,
        variables: {
          input: { workflows: { id, is_active: !currentActive } },
        },
      });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, is_active: !currentActive } : r)));
      incLoadCount();
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleDelete = useCallback(async (row) => {
    const ok = window.confirm(`Delete workflow "${row.name}"?`);
    if (!ok) return;
    try {
      updateBusy(row.id, true);
      if (row.id === sidebarItem?.id) {
        setSidebarItem();
      }
      await client.graphql({
        query: upsertWorkflow,
        variables: {
          input: { workflows: { id: row.id } },
          input_delete: [{ id: row.id, key: "workflows" }],
        },
      });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      console.error(e);
    } finally {
      updateBusy(row.id, false);
    }
  }, [sidebarItem]);

  const handleSort = field => () => {
    if (sortField === field) {
      setSortOrder(prevState => ({ ...prevState, [sortField]: prevState[sortField] === 'asc' ? 'desc' : 'asc' }));
    } else {
      setSortOrder(prevState => ({ ...prevState, [field]: prevState[field] || 'asc' }));
      setSortField(field);
    }
    setDefaults();
  };

  const sortIcon = sortOrder[sortField] === 'asc' ? upIcon : downIcon;

  // ------- Derived -------
  const fmtCurrency = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }),
    []
  );

  const stickyThStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "#F4F4F4",
    zIndex: 2,
    fontSize: "0.875rem",
    textAlign: "left"
  };

  const idCellStyle = { borderRight: "1px solid #ccc", padding: "0 12px", width: 90  };
  const nameCellStyle = { borderRight: "1px solid #ccc", padding: "0 12px" }; 
  const isActiveCellStyle = { borderRight: "1px solid #ccc", padding: "0 12px", width: 100 };                                
  const lastCellStyle = { padding: "0 12px", width: 220 };                                

  const workflow_breakout = workflow => {
    let tasks_json;
    try { tasks_json = JSON.parse(workflow.tasks_json); } catch (_) {};
    let next_touchpoint_counts = workflow.next_touchpoint_counts_json;
    try { next_touchpoint_counts = JSON.parse(next_touchpoint_counts); } catch (_) {};
    const get_count = task_id => {
      return (next_touchpoint_counts || []).find(e => e.next_task_id === task_id)?.count || <>&ndash;</>;
    }
    return (
      <div onClick={event => event.stopPropagation()}>
        <Link
          to={`/app/customers/workflows/${workflow.id}`}
          className="text-decoration-none fg-purple"
         >
          <h5 className="mb-3 fg-purple text-break"><b>{workflow.name}</b></h5>
        </Link>
        {(tasks_json || []).length > 0 && (
          <>
            <div className="row g-0">
              <div className="col"><u><b>Task</b></u></div>
              <div className="col-auto"><u><b>Count</b></u></div>
            </div>
            {(tasks_json || []).map((e, i) => (
              <Link
                className="text-decoration-none fg-purple"
                key={i}
                to={`/app/customers/workflows/subjects?workflow_id=${workflow.id}&workflow_name=${encodeURIComponent(
                  workflow.name || ''
                )}&next_task_id=${e.id}&next_touchpoint=${encodeURIComponent(
                  e.purpose || ''
                )}`}
              >
                <div className="row g-0">
                  <div className="col">{e.purpose}</div>
                  <div className="col-auto">{get_count(e.id)}</div>
                </div>
              </Link>
            ))}
            <hr className="my-2" />
            </>
        )}
        <Link
          className="text-decoration-none fg-purple"
          to={`/app/customers/workflows/subjects?workflow_id=${workflow.id}&workflow_name=${encodeURIComponent(
            workflow.name || ''
          )}`}
        >
          <div className="row g-0">
            <div className="col"><b>Total Subjects</b></div>
            <div className="col-auto">{workflow.subject_count}</div>
          </div>
        </Link>
      </div>
    );
  };

  const table = useMemo(() => (
    <div className="row g-0">
      <div className="col pe-0">
        {/* Table */}
        <div className="border rounded-3 overflow-auto" style={{ maxHeight: `calc(100vh - 150px)` }}>
          <table
            className="table table-sm table-borderless align-middle mb-0"
            onClick={event => event.stopPropagation()}
            style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", tabIndex: "0", tableLayout: "auto" }}
          >
            <thead className="text-muted small" style={{ height: "30px" }}>
              <tr className="border-bottom">
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('id')}>ID{sortField === 'id' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('name')}>Name{sortField === 'name' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3">Segment</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3">Recruitment Source</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('is_active')}>Active{sortField === 'is_active' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('updated_at')}>Updated At{sortField === 'updated_at' && sortIcon}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r) => {
                // Collect all referenced ids within subject_filters_json
                let rs = [];
                let st = [];
                let subject_filters_json;
                try { subject_filters_json = JSON.parse(r.subject_filters_json) } catch (_) {};
                if (subject_filters_json?.[0]?.subject_recruitment_sources?.recruitment_id) {
                    rs = (subject_filters_json[0].subject_recruitment_sources.recruitment_id.in || []).map(id => recruitmentSources[id]?.source_name || '').sort();
                }
                if (subject_filters_json?.[0]?.segments?.id) {
                    st = (subject_filters_json[0].segments.id.in || []).map(id => segments[id]?.name || '').sort();
                }
                return (
                  <tr
                    className={`position-relative cursor-default border-bottom ${sidebarItem?.id === r.id ? 'bw-focus-ring' : ''}`}
                    key={r.id}
                    onClick={event => { event.stopPropagation(); setSidebarItem(r); }}
                    style={{ height: "30px", tabIndex: 0 }}
                  >
                    {/* ID */}
                    <td
                      className="text-start align-middle py-2 ps-3"
                      style={idCellStyle}
                    >
                      <Link
                        to={`/app/customers/workflows/${r.id}`}
                        className="text-decoration-none"
                        style={{ color: "inherit" }}
                      >
                        {r.id}
                        {busy[r.id] ? (
                          <SpinnerLineDark />
                        ) : undefined}
                      </Link>
                    </td>

                    {/* Name */}
                    <td className="text-start align-middle py-2 px-3" style={{ borderRight: "1px solid #ccc" }}>
                      <Link
                        to={`/app/customers/workflows/${r.id}`}
                        className="text-decoration-none fg-purple"
                      >
                        <b>{r.name}</b>
                      </Link>
                    </td>

                    {/* Segment */}
                    <td
                      className="text-start align-middle py-2 ps-3"
                      style={{ borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: '250px', wordWrap: 'break-word', whiteSpace: 'normal' }}
                    >
                      {st.length > 0 ? st.map((e, i) => <React.Fragment key={i}><span>{e}</span><br /></React.Fragment>) : dash}
                    </td>

                    {/* Recruiting Source */}
                    <td className="text-start align-middle py-2 ps-3"  style={{ borderRight: "1px solid #ccc", padding: "0 12px" }}>
                      {rs.length > 0 ? rs.map((e, i) => <React.Fragment key={i}><span>{e}</span><br /></React.Fragment>) : dash}
                    </td>

                    {/* Status */}
                    <td className="text-start align-middle py-2 ps-3" style={isActiveCellStyle}>
                      <div className="form-switch d-inline-block">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          defaultChecked={r.is_active}
                          onChange={() => toggleActive(r.id, r.is_active)}
                          style={{
                            backgroundColor: r.is_active ? "green" : "red",
                            borderColor: r.is_active ? "green" : "red",
                          }}
                        />
                      </div>
                    </td>

                    {/* Updated At + Actions */}
                    <td className="align-middle py-2 ps-3" style={lastCellStyle}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                          {r.updated_at ? moment(r.updated_at).fromNow() : "—"}
                        </span>

                        <div className="d-flex align-items-center">
                          <Link
                            style={{ color: "var(--bs-body-color)" }}
                            to={`/app/workflows/${r.id}?workflow_id=${r.id}`}
                          >
                            <FontAwesomeIcon
                              className="cursor-pointer me-3"
                              icon={icons_solid.faChartSimple}
                              title="Analytics"
                            />
                          </Link>

                          <Link
                            style={{ color: "var(--bs-body-color)" }}
                            to={`/app/customers/workflows/${r.id}?workflow_id=${r.id}`}
                          >
                            <FontAwesomeIcon
                              className="cursor-pointer me-3"
                              icon={icons.faPenToSquare}
                              title="Edit"
                            />
                          </Link>

                          <FontAwesomeIcon
                            className="cursor-pointer text-danger"
                            icon={icons.faTrashCan}
                            onClick={event => { event.stopPropagation(); handleDelete(r); }}
                            title="Delete"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>

          </table>
        </div>
      </div>
      {sidebarItem && (
        <div className="col-auto ps-4 width_300">
          <div className="position-sticky" style={{maxHeight: '100vh', overflow: 'auto', top: '0px'}}>
            <button aria-label="Close" className="btn-close float-end" onClick={() => setSidebarItem()} type="button"></button>
              {workflow_breakout(sidebarItem)}
          </div>
        </div>
      )}
    </div>
  ), [loadCount, sidebarItem, isLoadingMore, JSON.stringify(busy), recruitmentSources, rows, segments]);

  return (
    <div className="container-fluid px-4">
      <Header path="/app/customers/">
        {/* Header */}
        <div className="row align-items-center mt-3 mb-3 g-0">
          <div className="col">
            <h4 className="mb-0 ps-4" style={{ color: '#2E2868' }}>Workflows</h4>
          </div>
          {/* Search Box */}
          <div className="col-auto ms-auto pe-0 position-relative">
            <input
              autoFocus
              className="form-control width_200"
              defaultValue={search}
              key={searchCount}
              placeholder="Search..."
              onChange={(e) => debounceSearch(e.target.value)}
            />
            <i
              className="bi bi-x position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
              onClick={() => {
                setSearch("");
                incSearchCount();
              }}
              role="button"
            ></i>
          </div>

          {/* Create New Workflow Button */}
          <div className="col-auto">
            <Link to="/app/customers/workflows/new">
              <button
                className="btn btn-primary"
                style={{ backgroundColor: "#2E2868", borderColor: "#2E2868", marginLeft: "20px" }}
              >
                Create New Workflow
              </button>
            </Link>
          </div>
        </div>
        <hr />
      </Header>

      {rows?.length ? (
        <AnimatePresence initial={true}>
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1.0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            {table}
      			{loadMoreButton}
          </motion.div>
        </AnimatePresence>
      ) : undefined}

    </div>
  );
};
